import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AsyncPipe, KeyValuePipe, TitleCasePipe } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';

import { combineLatest, takeUntil } from 'rxjs';

import { CityDetails } from 'src/app/core/types/models/city-details.model';
import { CityResponse } from 'src/app/core/types/models/city-response.model';
import { CountryDetails } from 'src/app/core/types/models/country-details.model';
import { CountryResponse } from 'src/app/core/types/models/country-response.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { ERoutes } from 'src/app/core/enums/routes.enum';
import { EInfoType } from 'src/app/core/enums/country-info-type.enum';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import {
  IViatorCategories,
  ViatorComponent,
} from 'src/app/shared/used-components/viator/viator.component';

import { ThingsToDoService } from '../../../services/things-to-do.service';
import { CloudinaryService } from 'src/app/core/services/cloudinary.service';
import { ScrollToIdService } from 'src/app/core/services/scroll-to-id.service';
import { CountryInfoService } from 'src/app/core/services/country-info.service';
import { VoiceMapResponse } from 'src/app/core/types/models/voice-map-response';
import { CountryInfoTextService } from '../../../services/country-info-text.service';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';
import {
  HOTEL_CLIENT,
  HotelsBookingContainerComponent,
} from 'src/app/shared/used-components/hotels-booking-container/hotels-booking-container.component';
import { HotelsProviderServiceService } from 'src/app/shared/used-components/hotels-booking-container/hotels-provider-service.service';
import { PageInfoComponent } from '../../../pages/page-info';

import { Config } from 'src/app/core/configs/config';
import { SafeUrlPipe } from 'src/app/shared/pipes/safe-url.pipe';
import { ReplaceStrPipe } from 'src/app/shared/pipes/replace-str.pipe';
import { ParseCoordinatesPipe } from 'src/app/shared/pipes/parse-coordinates.pipe';

import { GalleryComponent } from 'src/app/shared/components/gallery/gallery.component';
import { SimpleMapComponent } from 'src/app/shared/maps/simple-map/simple-map.component';
import { TripsListComponent } from '../../../components/trips-list/trips-list.component';
import { PlacesListComponent } from '../../../components/places-list/places-list.component';
import { AudioToursComponent } from 'src/app/shared/used-components/audio-tours/audio-tours.component';
import { TextEllipsisComponent } from 'src/app/shared/components/text-ellipsis/text-ellipsis.component';
import { BookingBannerComponent } from 'src/app/shared/components/booking-banner/booking-banner.component';
import { SimpleBreadcrumbComponent } from '../../../components/simple-breadcrumb/simple-breadcrumb.component';
import { OccasionGeniusComponent } from 'src/app/shared/components/occasion-genius/occasion-genius.component';
import { PopularItinerariesComponent } from 'src/app/shared/components/popular-itineraries/popular-itineraries.component';
import { SearchInputFieldComponent } from 'src/app/shared/components/forms/search-input-field/search-input-field.component';

@Component({
  selector: 'app-things-to-do-desktop',
  templateUrl: './things-to-do-desktop.component.html',
  styleUrls: ['../../../country-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CountryInfoService,
    ThingsToDoService,
    CountryInfoTextService,
    OccasionGeniusService,
    {
      provide: HOTEL_CLIENT,
      useClass: HotelsProviderServiceService,
    },
  ],
  imports: [
    TranslateModule,
    ReplaceStrPipe,
    TitleCasePipe,
    NgxSkeletonLoaderModule,
    KeyValuePipe,
    RouterModule,
    ParseCoordinatesPipe,
    SafeUrlPipe,
    AsyncPipe,
    ViatorComponent,
    GalleryComponent,
    SimpleMapComponent,
    TripsListComponent,
    PlacesListComponent,
    AudioToursComponent,
    TextEllipsisComponent,
    BookingBannerComponent,
    OccasionGeniusComponent,
    SimpleBreadcrumbComponent,
    SearchInputFieldComponent,
    PopularItinerariesComponent,
    HotelsBookingContainerComponent,
  ],
})
export class ThingsToDoDesktopComponent
  extends PageInfoComponent
  implements OnInit, OnDestroy
{
  public readonly eSvgIcons = ESvgIcons;
  public readonly ERoutes = ERoutes;
  public readonly eInfoType = EInfoType;

  public breadcrumbs: { text: string; path: string }[] = [];
  public menuOpened: boolean[] = [false, false, false];
  public numOfHurt: number = 0;

  public countryResponse: CountryResponse;
  public countryDetails: CountryDetails;
  public cityResponse: CityResponse;
  public cityDetails: CityDetails;
  public voiceMapResponse: VoiceMapResponse;
  public occasionGeniusResponse: OccasionGeniusResponse =
    new OccasionGeniusResponse();
  public isFetching: boolean;
  public categoriesResponse: IViatorCategories = {};

  public bannerImgUrl: string = `${Config.imgCdnUrl}images/country-info_banner.png`;

  constructor(
    protected cloudinaryService: CloudinaryService,
    protected thingsToDoService: ThingsToDoService,
    protected countryInfoTextService: CountryInfoTextService,
    protected countryInfoService: CountryInfoService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdr: ChangeDetectorRef,
    protected scrollToIdService: ScrollToIdService,
  ) {
    super(cdr, scrollToIdService);

    this.bannerImgUrl = this.cloudinaryService.getCloudImage(
      this.bannerImgUrl,
      true,
    );

    combineLatest([
      this.thingsToDoService.countryResponse$,
      this.thingsToDoService.cityResponse$,
      this.thingsToDoService.categoriesResponse$,
      this.thingsToDoService.voiceMapResponse$,
      this.thingsToDoService.occasionGeniusResponse$,
      this.thingsToDoService.isFetching$,
    ])
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        ([
          countryResponse,
          cityResponse,
          categoriesResponse,
          voiceMapResponse,
          occasionGeniusResponse,
          isFetching,
        ]) => {
          this.countryResponse = countryResponse;
          this.cityResponse = cityResponse;
          this.categoriesResponse = categoriesResponse;
          this.voiceMapResponse = voiceMapResponse;
          if (occasionGeniusResponse) {
            this.occasionGeniusResponse = occasionGeniusResponse;
          }
          this.isFetching = isFetching;
          this.countryDetails =
            this.countryResponse.countryInfo.getFirstCountry();
          this.cityDetails = this.cityResponse.cityInfo.getFirstCity();
          this.cdr.markForCheck();
        },
      );
  }

  ngOnInit() {
    this.breadcrumbs = this.thingsToDoService.generateBreadcrumbs(
      this.route.snapshot.url[0].path,
    );
    this.defaultLocationName = this.breadcrumbs[0].text
      .replace('things-to-do-in-', '')
      .replace(/-/g, ' ');
    this.thingsToDoService.fetchData(this.route.snapshot.url, (breadcrumbs) => {
      this.breadcrumbs = [...breadcrumbs, ...this.breadcrumbs];
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  flyToGalleryItem(item: any): void {
    if (item.coordinates) {
      this.cityResponse.destinations[0].coordinates = item.coordinates;
    }
  }

  public planAPerfectTrip(): void {
    const { name, place_id } = this.cityDetails;
    const countryId = this.countryDetails.place_id;
    this.countryInfoService.setPerfectTripState(place_id, countryId, name);
    this.router.navigate([ERoutes.SLASH, ERoutes.TRIP_PLANNER]).then();
  }
}
